import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stepper } from "../components/shared/Stepper/Stepper";
import {
  type NameLocationFormData,
  NameLocationStep,
} from "../components/onboarding/steps/NameLocationStep";
import {
  type CredentialsFormData,
  CredentialsStep,
} from "../components/onboarding/steps/CredentialsStep";
import { useAuthStore } from "../store/authStore";
import { BillingStep } from "../components/onboarding/steps/BillingStep";

type StepData = {
  0: NameLocationFormData;
  2: object;
  1: CredentialsFormData;
  3: object;
};

type CurrentStep = keyof StepData;

const initialData: StepData = {
  "0": {
    address: {
      city: "",
      country: "",
      phone: "",
      state: "",
      street: "",
      zipCode: "",
    },
    firstName: "",
    lastName: "",
    middleName: "",
  },
  "1": {
    license: { expiration: "", number: "", state: "AL" },
    practiceType: "solo",
    role: "",
  },
  "2": {},
  "3": {},
};

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [stepData, setStepData] = useState<StepData>(initialData);
  const { user } = useAuthStore();
  const steps = ["Name & Location", "Credentials", "Billing Info", "Welcome"];

  const handleNext = <T extends CurrentStep>(
    data: StepData[T],
    stepCount: number = 1
  ) => {
    const nextActiveStep = Math.min(activeStep + stepCount, steps.length);

    setActiveStep(nextActiveStep);
    setStepData((prevValue) => {
      return {
        ...prevValue,
        [activeStep]: data,
      };
    });
  };

  const handleBack = (stepCount: number = -1) => {
    const nextActiveStep = Math.max(activeStep + stepCount, 0);

    setActiveStep(nextActiveStep);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900">
            Complete Your Profile
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Let's get your practice set up. Please complete the following steps
            to get started.
          </p>
        </div>

        <div className="mb-12">
          <Stepper steps={steps} activeStep={activeStep} />
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6">
          {activeStep === 0 && (
            <NameLocationStep
              onNext={(data) => handleNext(data)}
              initialData={stepData[activeStep]}
            />
          )}

          {activeStep === 1 && (
            <CredentialsStep
              onBack={() => handleBack()}
              onNext={(data) => handleNext(data)}
              initialData={stepData[activeStep]}
            />
          )}

          {activeStep === 2 && (
            <BillingStep onBack={() => handleBack()} onNext={() => {}} />
          )}
          {/* Add other step components */}
        </div>
      </div>
    </div>
  );
};
